import React, { useState, useEffect } from "react";
import '../../assets/styles/ClientShared.css';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const WebContent = () => {
  const [activePage, setActivePage] = useState("home");
  const [webContent, setWebContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState(null);

  const pages = [
    {
      id: "home",
      title: "Home",
      sections: [
        { id: "header", label: "Header", example: "Welcome to [Your Business Name]!" },
        { id: "intro", label: "Intro", example: "We specialize in creating amazing experiences." },
        { id: "callToAction", label: "Call to Action", example: "Contact us today to get started!" },
      ],
    },
    {
      id: "about",
      title: "About Us",
      sections: [
        { id: "mission", label: "Mission", example: "Our mission is to [Your Mission Statement]." },
        { id: "vision", label: "Vision", example: "We envision a world where [Your Vision Statement]." },
        { id: "team", label: "Team", example: "Meet our amazing team of experts." },
      ],
    },
    {
      id: "contact",
      title: "Contact",
      sections: [
        { id: "address", label: "Address", example: "123 Main Street, City, Country." },
        { id: "phone", label: "Phone", example: "+1-234-567-8901" },
        { id: "email", label: "Email", example: "contact@yourbusiness.com" },
      ],
    },
    {
      id: "services/products",
      title: "Services/Products",
      sections: [
        { id: "overview", label: "Overview", example: "We offer a wide range of services including..." },
        { id: "service1", label: "Service 1", example: "Describe your first key service here." },
        { id: "service2", label: "Service 2", example: "Describe your second key service here." },
      ],
    },
    {
      id: "portfolio",
      title: "Portfolio",
      sections: [
        { id: "showcaseIntro", label: "Showcase Intro", example: "Explore some of our best work below." },
        { id: "project1", label: "Project 1", example: "Details about your first portfolio project." },
        { id: "project2", label: "Project 2", example: "Details about your second portfolio project." },
      ],
    },
  ];

  useEffect(() => {
    const fetchUserAndProject = async () => {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        toast.error("User is not authenticated.");
        return;
      }

      try {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setProjectId(userData.projectId);
        } else {
          toast.error("User document not found in Firestore.");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Error fetching user data. Please try again.");
      }
    };

    fetchUserAndProject();
  }, []);

  useEffect(() => {
    if (!projectId) return;

    const fetchWebContent = async () => {
      try {
        setLoading(true);
        const projectRef = doc(db, "projects", projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          setWebContent(projectData.webContent || {});
        } else {
          toast.error("Project document not found.");
        }
      } catch (error) {
        console.error("Error fetching web content:", error);
        toast.error("Failed to load web content. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchWebContent();
  }, [projectId]);

  const handleInputChange = (pageId, sectionId, value) => {
    setWebContent((prev) => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        [sectionId]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!projectId) {
      toast.error("Project ID is missing. Cannot save content.");
      return;
    }

    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        webContent,
      });
      toast.success("Web content saved successfully!");
    } catch (error) {
      console.error("Error saving web content:", error);
      toast.error("Failed to save web content. Please try again.");
    }
  };

  if (loading) {
    return <div className="cp-loading"><p>Loading...</p></div>;
  }

  return (
    <div className="cp-page">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Web Content</h1>
        <p className="cp-subtitle">Write content for each section of your website.</p>
      </div>
      <div className="cp-section">
        {/* Tab bar */}
        <div className="cp-tab-bar">
          {pages.map(page => (
            <button key={page.id} className={`cp-tab${activePage === page.id ? ' active' : ''}`} onClick={()=>setActivePage(page.id)}>
              {page.title}
            </button>
          ))}
        </div>
        {/* Active page content */}
        <div className="cp-section-body">
          {pages.find(p=>p.id===activePage)?.sections.map(({id,label,example}) => (
            <div key={id} className="cp-form-group">
              <label>{label} <span style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',color:'var(--text-tertiary)',fontWeight:400}}>(e.g. "{example}")</span></label>
              <textarea value={webContent[activePage]?.[id]||''} onChange={e=>handleInputChange(activePage,id,e.target.value)} placeholder={`Write content for ${label}`}/>
            </div>
          ))}
          <div className="cp-btn-row">
            <button className="cp-btn-primary" onClick={handleSave}>Save Content</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebContent;
